import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  ShieldCheck,
  Settings,
  Factory,
  Truck,
  Clock,
  Package,
  CheckCircle2,
  Building2,
  Hammer,
  MessageCircle,
} from "lucide-react";
import { PRODUCTS } from "../lib/products";
import QuoteDialog from "../components/QuoteDialog";
import InstagramFeed from "../components/InstagramFeed";
import { WHATSAPP_NUMBER } from "../lib/api";

import SEO from "../components/SEO";
const ORANGE_RUBBER_GLOVES =
  "https://static1.industrybuying.com/products/safety/safety-gloves/rubber-gloves/SAF.RUB.720329000_1690179436439.webp";

const GOVERNMENT_CLIENTS = [
  {
    name: "BHEL",
    logo: "https://upload.wikimedia.org/wikipedia/commons/8/85/BHEL.png",
  },
  {
    name: "BEML",
    logo: "https://images.moneycontrol.com/static-mcnews/2025/02/20250210155342_Screenshot_20250210-212225_Gallery-642x435.jpg",
  },
  {
    name: "Indian Railways",
    logo: "https://logowik.com/content/uploads/images/indian-railways-blue2669.logowik.com.webp",
  },
  {
    name: "MECL",
    logo: "https://www.jobyaari.com/public/notification/organisation_logo/Mineral%20Exploration%20and%20Consultancy%20Limited%20%28MECL%29.jpg",
  },
];

const PRIVATE_CLIENTS = [
  {
    name: "Haldiram's",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/25/Haldiram%27s_Logo_SVG.svg/3840px-Haldiram%27s_Logo_SVG.svg.png",
  },
  {
    name: "Sunder Biscuits",
    logo: "https://media.potatopro.com/sunder-biscuits-industry-1200x589.jpg",
  },
];

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState(null);

  const featured = useMemo(() => {
    const slugs = [
      "softcare",
      "knitted-hand-gloves",
      "spectrum-karveer",
      "canvas-hand-gloves",
    ];

    return slugs
      .map((slug) => PRODUCTS.find((p) => p.slug === slug))
      .filter(Boolean);
  }, []);

  const knitted = PRODUCTS.find((p) => p.slug === "knitted-hand-gloves");
  const dotted = PRODUCTS.find((p) => p.slug === "knitted-dotted-hand-gloves");
  const coated =
    PRODUCTS.find((p) => p.slug === "spectrum-karveer") ||
    PRODUCTS.find(
      (p) => p.productCategory === "Industrial Coated Hand Gloves"
    );

  const openQuote = (product) => {
    setQuoteProduct(product);
    setQuoteOpen(true);
  };

  const generalWhatsApp = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hello RSN Gloves, I would like to know more about your glove range and bulk supply."
  )}`;

  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RSN Gloves",
    url: "https://www.rsngloves.com",
    logo: "https://www.rsngloves.com/images/brand/rsn-logo.webp",
    description:
      "Indian hand gloves and safety gloves supplier with a core range of domestically manufactured rubber hand gloves, plus cotton and industrial coated gloves.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91-8928341661",
      contactType: "sales",
      areaServed: "IN",
    },
  };

  return (
    <>
      <SEO
        title="Hand Gloves & Safety Gloves Manufacturer in India | RSN Gloves"
        description="RSN Gloves supplies hand gloves and safety gloves across India, with domestically manufactured rubber hand gloves plus cotton and industrial coated gloves for bulk buyers."
        path="/"
        jsonLd={homeJsonLd}
      />
    <div data-testid="home-page" className="home-page overflow-x-hidden bg-white">
      {/* 01 — COMING SOON HERO */}
      <section
        className="rsn-launch-hero"
        aria-label="RSN Rubber Hand Gloves Coming Soon"
      >
        <img
          src="/images/brand/rsn-coming-soon-hero.png"
          alt="RSN Rubber Hand Gloves Coming Soon - Made for Hands That Build India"
          className="rsn-launch-hero-image"
          fetchPriority="high"
        />
      </section>

      {/* STATIC BRAND STRIP */}
      <div className="home-trust-strip" aria-label="RSN Gloves trust highlights">
        {[
          "MADE FOR HANDS THAT BUILD INDIA",
          "PROUDLY MADE IN INDIA",
          "DIRECT FROM MANUFACTURER",
          "PAN-INDIA DISPATCH",
          "BULK INDUSTRIAL SUPPLY",
        ].map((item) => (
          <span key={item} className="home-trust-strip-item">
            <span className="home-ticker-dot" /> {item}
          </span>
        ))}
      </div>

      {/* 02 — BRAND PROMISE / CURRENT PRODUCT VISUALS */}
      <section id="protection" className="home-protection-section relative isolate overflow-hidden bg-[#F4F8F5]">
        <div className="absolute inset-0 home-grid opacity-35" />

        <div className="relative z-10 max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[0.92fr_1.08fr] gap-10 lg:gap-14 items-center w-full py-12 lg:py-8">
          <div className="max-w-3xl">
            <div className="section-eyebrow text-[#075B3A]">OUR PURPOSE</div>

            <h1 className="font-display protection-title text-[#0F172A] mt-5">
              Protection
              <br />
              for Every Hand
              <br />
              That <span className="text-[#0B6B47]">Builds India.</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-[#4E5F57] max-w-2xl leading-relaxed">
              RSN Gloves supplies hand gloves and safety gloves for industrial, commercial
              and everyday work across India — including rubber hand gloves, cotton hand
              gloves and industrial coated gloves.
            </p>

            <p className="mt-3 text-xl sm:text-2xl font-bold text-[#0F172A] max-w-xl">
              We make gloves for those hands.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mt-8 max-w-3xl">
              <LightBenefit icon={ShieldCheck} title="Reliable" text="Protection" />
              <LightBenefit icon={Factory} title="Proudly" text="Made in India" />
              <LightBenefit icon={Settings} title="Built for" text="Indian Industry" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/products" className="home-btn home-btn-orange">
                Explore Our Range <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={generalWhatsApp}
                target="_blank"
                rel="noreferrer"
                className="home-btn home-btn-green-outline"
              >
                <MessageCircle className="w-4 h-4" /> Talk to Us
              </a>
            </div>
          </div>

          <div className="protection-showcase" aria-label="RSN Gloves product range">
            <div className="protection-showcase-copy">
              <span>RUBBER</span>
              <span>COTTON</span>
              <span>COATED</span>
            </div>

            <div className="protection-product protection-product-rubber">
              <img
                src={ORANGE_RUBBER_GLOVES}
                alt="Orange rubber hand gloves"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>

            {knitted?.image && (
              <div className="protection-product protection-product-cotton">
                <img src={knitted.image} alt="Knitted cotton hand gloves" />
              </div>
            )}

            {coated?.image && (
              <div className="protection-product protection-product-coated">
                <img src={coated.image} alt="Industrial coated hand gloves" />
              </div>
            )}

            <div className="protection-showcase-message">
              <strong>Made for Hands That Build India.</strong>
              <span>Three categories. One promise: dependable hand protection.</span>
            </div>
          </div>
        </div>
      </section>

      {/* 03 — DOMESTIC MANUFACTURING USP — KEPT AS APPROVED */}
      <section className="home-screen home-made-here relative isolate overflow-hidden bg-[#064E3B] text-white">
        <div className="absolute inset-0 home-made-grid opacity-50" />
        <div className="home-made-glow" />
        <div className="absolute -right-16 lg:right-6 top-1/2 -translate-y-1/2 font-display font-black text-[32vw] lg:text-[23vw] leading-none text-white/[0.035] select-none pointer-events-none">
          INDIA
        </div>

        <div className="relative z-10 max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20 w-full">
          <div className="max-w-5xl">
            <div className="section-eyebrow text-[#B7F171]">THE RSN ADVANTAGE</div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-[84px] font-black leading-[0.92] tracking-[-0.045em] mt-5">
              Made here.
              <br />
              <span className="text-[#B7F171]">Not imported.</span>
            </h2>
            <p className="mt-7 max-w-3xl text-lg sm:text-xl text-white/76 leading-relaxed">
              In a market heavily dependent on imported rubber gloves, RSN&apos;s domestic
              manufacturing gives buyers something that matters just as much as price:
              dependable supply.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold">
              <CheckCircle2 className="w-4 h-4 text-[#B7F171]" />
              One of only two domestic manufacturers in our category
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-6 mt-12 lg:mt-16">
            <AdvantageCard
              number="01"
              icon={Package}
              title="Reliable Availability"
              text="Less dependence on container arrivals, overseas shipping schedules and international disruptions."
            />
            <AdvantageCard
              number="02"
              icon={Settings}
              title="Better Price Stability"
              text="Domestic production gives us greater control over supply planning and helps reduce import-linked volatility."
            />
            <AdvantageCard
              number="03"
              icon={Truck}
              title="Faster Replenishment"
              text="Production and dispatch can be planned around the requirements of Indian distributors and industrial buyers."
            />
          </div>

          <div className="mt-12 lg:mt-16 border-t border-white/15 pt-8">
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold max-w-5xl leading-tight">
              Indian manufacturing isn&apos;t just where our gloves come from.
              <span className="text-[#B7F171]"> It is our competitive advantage.</span>
            </p>
          </div>
        </div>
      </section>

      {/* 04 — CLIENT LOGOS */}
      <section className="home-clients relative overflow-hidden bg-[#F8FAF8] border-y border-[#E1E9E4]">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-14 items-end">
            <div>
              <div className="section-eyebrow text-[#075B3A]">TRUSTED WHERE RELIABILITY MATTERS</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.04em] leading-[0.98] text-[#0F172A] mt-4">
                Supplying India&apos;s
                <br />
                leading institutions.
              </h2>
            </div>
            <div className="lg:pb-1">
              <p className="text-lg text-[#536159] leading-relaxed max-w-2xl">
                From government and public-sector organisations to established private
                businesses, these are only a few of the customers we currently supply.
              </p>
            </div>
          </div>

          <div className="mt-10 space-y-5">
            <div className="client-band client-logo-band">
              <div className="client-band-label">
                <Building2 className="w-5 h-5" /> Government &amp; Public Sector
              </div>
              <div className="client-logo-grid government">
                {GOVERNMENT_CLIENTS.map((client) => (
                  <ClientLogo key={client.name} client={client} />
                ))}
              </div>
            </div>

            <div className="client-band client-logo-band">
              <div className="client-band-label">
                <Hammer className="w-5 h-5" /> Private Industry
              </div>
              <div className="client-logo-grid private">
                {PRIVATE_CLIENTS.map((client) => (
                  <ClientLogo key={client.name} client={client} />
                ))}
                <div className="client-logo-card client-more-card">
                  <strong>AND MORE</strong>
                  <span>Selected customers shown</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 05 — PRODUCT RANGE */}
      <section className="home-products-section bg-white">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-9">
            <div>
              <div className="section-eyebrow text-[#075B3A]">OUR PRODUCT RANGE</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.04em] leading-[0.98] text-[#0F172A] mt-4">
                One range. Every kind of work.
              </h2>
              <p className="mt-4 text-lg text-[#5A6861] max-w-2xl">
                From everyday rubber protection to cotton handling gloves and advanced
                nitrile-coated cut resistance.
              </p>
            </div>
            <Link to="/products" className="inline-flex items-center gap-2 font-bold text-[#075B3A] hover:text-[#04412B]">
              View all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
            <CategoryCard
              to="/products?category=Rubber%20Hand%20Gloves"
              index="01"
              title="Rubber Hand Gloves"
              text="Our core manufactured range for industrial, commercial and everyday protection."
              tone="rubber"
              images={[ORANGE_RUBBER_GLOVES]}
            />

            <CategoryCard
              to="/products?category=Cotton%20Hand%20Gloves"
              index="02"
              title="Cotton Hand Gloves"
              text="Knitted, dotted, jeans, canvas and leather-reinforced hand protection."
              tone="cotton"
              images={[knitted?.image, dotted?.image].filter(Boolean)}
            />

            <CategoryCard
              to="/products?category=Industrial%20Coated%20Hand%20Gloves"
              index="03"
              title="Industrial Coated Hand Gloves"
              text="Commercial coated and Spectrum nitrile cut-resistant gloves for demanding work."
              tone="coated"
              images={coated ? [coated.image] : []}
            />
          </div>
        </div>
      </section>

      {/* 06 — FEATURED PRODUCTS — KEPT */}
      <section className="bg-[#F5F8F6] border-y border-[#E3EAE6]">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
            <div>
              <div className="section-eyebrow text-[#075B3A]">FEATURED PRODUCTS</div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-[-0.04em] text-[#0F172A] mt-4">
                Built to work. Ready to supply.
              </h2>
            </div>
            <div className="text-sm text-[#647169]">Bulk RFQs · Pan-India dispatch</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((product) => (
              <FeaturedProductCard key={product.slug} product={product} onQuote={openQuote} />
            ))}
          </div>
        </div>
      </section>

      {/* 07 — WHY BUY DIRECT — KEPT */}
      <section className="relative overflow-hidden bg-[#0B1728] text-white">
        <div className="absolute inset-0 home-grid-dark opacity-40" />
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20 relative z-10">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
            <div>
              <div className="section-eyebrow text-[#84CC16]">DIRECT FROM MANUFACTURER</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.04em] leading-[0.98] mt-4 max-w-4xl">
                Better control over supply.
                <br />
                Better confidence for buyers.
              </h2>
              <p className="mt-5 text-lg text-white/70 max-w-3xl">
                Whether you are a wholesaler, retailer, industrial buyer or institutional
                purchaser, talk directly to the team behind the supply.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { Icon: Factory, title: "Manufacturer", text: "Domestic production" },
                { Icon: Clock, title: "5-min reply", text: "Fast WhatsApp response" },
                { Icon: Truck, title: "Pan-India", text: "Dispatch capability" },
                { Icon: Package, title: "Bulk RFQs", text: "Trade & institutional supply" },
              ].map(({ Icon, title, text }) => (
                <div key={title} className="direct-stat-card">
                  <Icon className="w-5 h-5 text-[#84CC16]" />
                  <div className="font-display font-bold text-xl mt-4">{title}</div>
                  <div className="text-sm text-white/55 mt-1">{text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 08 — INSTAGRAM — KEPT */}
      <div className="bg-white">
        <InstagramFeed />
      </div>

      <QuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        product={quoteProduct}
      />
    </div>
    </>
  );
}

function LightBenefit({ icon: Icon, title, text }) {
  return (
    <div className="light-benefit">
      <div className="light-benefit-icon">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div>
        <div className="font-bold text-sm text-[#0F172A]">{title}</div>
        <div className="text-xs text-[#65746C] mt-0.5">{text}</div>
      </div>
    </div>
  );
}

function AdvantageCard({ number, icon: Icon, title, text }) {
  return (
    <article className="advantage-card">
      <div className="flex items-start justify-between gap-4">
        <div className="advantage-icon">
          <Icon className="w-6 h-6" strokeWidth={1.7} />
        </div>
        <span className="font-display text-sm font-black text-white/25">{number}</span>
      </div>
      <h3 className="font-display text-2xl font-black mt-8">{title}</h3>
      <p className="mt-3 text-sm sm:text-base text-white/65 leading-relaxed">{text}</p>
    </article>
  );
}

function ClientLogo({ client }) {
  return (
    <div className="client-logo-card" title={client.name}>
      <img
        src={client.logo}
        alt={`${client.name} logo`}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={(event) => {
          event.currentTarget.style.display = "none";
          const fallback = event.currentTarget.nextElementSibling;
          if (fallback) fallback.style.display = "block";
        }}
      />
      <span className="client-logo-fallback">{client.name}</span>
    </div>
  );
}

function CategoryCard({ to, index, title, text, tone, images }) {
  return (
    <Link to={to} className={`home-category-card-v3 category-v3-${tone}`}>
      <div className="category-v3-copy">
        <div className="category-v3-number">{index}</div>
        <h3 className="font-display text-2xl xl:text-3xl font-black tracking-[-0.035em] leading-tight text-[#0F172A]">
          {title}
        </h3>
        <p className="mt-3 text-sm text-[#58675F] leading-relaxed">{text}</p>
        <span className="category-v3-link">
          Explore Range <ArrowRight className="w-4 h-4" />
        </span>
      </div>

      <div className={`category-v3-images ${images.length > 1 ? "is-pair" : ""}`}>
        {images.map((image, idx) => (
          <img
            key={`${image}-${idx}`}
            src={image}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ))}
      </div>
    </Link>
  );
}

function FeaturedProductCard({ product, onQuote }) {
  return (
    <article className="featured-home-card">
      <Link to={`/products/${product.slug}`} className="featured-home-image-wrap">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="featured-home-image"
        />

        {product.cutResistance && (
          <span className="featured-home-badge">{product.cutResistance}</span>
        )}
      </Link>

      <div className="p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#748078]">
          {product.brand || product.series}
        </div>
        <h3 className="font-display text-xl font-black text-[#0F172A] mt-2 leading-tight">
          {product.name}
        </h3>
        <p className="text-sm text-[#69756F] mt-2 line-clamp-2">{product.tagline}</p>

        <div className="flex items-center gap-2 mt-5">
          <Link to={`/products/${product.slug}`} className="featured-details-btn">
            View Details
          </Link>
          <button type="button" onClick={() => onQuote(product)} className="featured-quote-btn">
            Quote
          </button>
        </div>
      </div>
    </article>
  );
}
